import { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, orderBy, limit, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const ReflectionsContext = createContext();

export function ReflectionsProvider({ children }) {
  const { currentUser } = useAuth();
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch user's reflections
  useEffect(() => {
    if (!currentUser) {
      setReflections([]);
      setLoading(false);
      return;
    }

    const fetchReflections = async () => {
      try {
        const reflectionsRef = collection(db, 'reflections');
        const q = query(
          reflectionsRef,
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          limit(10)
        );

        const querySnapshot = await getDocs(q);
        const reflectionsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp.toDate()
        }));

        setReflections(reflectionsData);
      } catch (error) {
        console.error('Error fetching reflections:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReflections();
  }, [currentUser]);

  // Add new reflection
  const addReflection = async (reflectionData) => {
    try {
      const reflectionsRef = collection(db, 'reflections');
      const docRef = await addDoc(reflectionsRef, {
        ...reflectionData,
        userId: currentUser.uid,
        timestamp: new Date()
      });

      const newReflection = {
        id: docRef.id,
        ...reflectionData,
        userId: currentUser.uid,
        timestamp: new Date()
      };

      setReflections(prev => [newReflection, ...prev]);
      return newReflection;
    } catch (error) {
      console.error('Error adding reflection:', error);
      throw error;
    }
  };

  const updateReflection = async (reflectionId, reflectionData) => {
    try {
      const reflectionRef = doc(db, 'reflections', reflectionId);
      await updateDoc(reflectionRef, {
        ...reflectionData,
        updatedAt: new Date()
      });
      
      setReflections(prev => prev.map(reflection => 
        reflection.id === reflectionId 
          ? { ...reflection, ...reflectionData }
          : reflection
      ));
    } catch (error) {
      console.error('Error updating reflection:', error);
      throw error;
    }
  };

  const deleteReflection = async (reflectionId) => {
    try {
      const reflectionRef = doc(db, 'reflections', reflectionId);
      await deleteDoc(reflectionRef);
      
      setReflections(prev => prev.filter(reflection => reflection.id !== reflectionId));
    } catch (error) {
      console.error('Error deleting reflection:', error);
      throw error;
    }
  };

  return (
    <ReflectionsContext.Provider value={{ 
      reflections, 
      loading, 
      addReflection,
      updateReflection,
      deleteReflection
    }}>
      {children}
    </ReflectionsContext.Provider>
  );
}

export function useReflections() {
  return useContext(ReflectionsContext);
} 